import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Pagination,
    Card,
    IconButton
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { TRASPASO_ESTADO } from "../../constants";
import EstadoBadge from "../ui/EstadoBadge";

/**
 * Vista mobile con lista paginada de traspasos
 */
export default function TraspasosMobileList({ rows, loading, onVer, onEditar, onTraspasar }) {
    const [page, setPage] = useState(1);
    const pageSize = 10;

    // Si cambian los rows (filtros), vuelve a la primera página
    useEffect(() => {
        setPage(1);
    }, [rows]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#cc071e" }} />
            </Box>
        );
    }

    if (!rows?.length) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No hay datos.
            </Alert>
        );
    }

    const totalPages = Math.ceil(rows.length / pageSize);
    const start = (page - 1) * pageSize;
    const pageRows = rows.slice(start, start + pageSize);

    // Color del acento (borde izquierdo) alineado a la paleta corporativa unificada
    const estadoColors = {
        'PENDIENTE': '#f39c12',
        'TRASPASADO': '#27ae60',
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Stack spacing={2}>
                {pageRows.map((r) => {
                    const estado = (r.estado ?? "").toUpperCase();
                    const isTraspasado = estado === TRASPASO_ESTADO.TRASPASADO;
                    const borderColor = estadoColors[estado] || '#95a5a6';

                    return (
                        <Card
                            key={r.id}
                            sx={{
                                borderLeft: `4px solid ${borderColor}`,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                },
                            }}
                        >
                            <Box sx={{ p: 2 }}>
                                {/* Header: ID y Estado */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: 700,
                                            color: "#212529",
                                        }}
                                    >
                                        #{r.id}
                                    </Typography>
                                    <EstadoBadge estado={r.estado} />
                                </Box>

                                {/* Almacenes origen → destino */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1.5,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#495057",
                                        }}
                                    >
                                        {r.almacenOrigenNombre || "-"}
                                    </Typography>
                                    <ArrowForwardIcon sx={{ fontSize: 16, color: "#6c757d" }} />
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#495057",
                                        }}
                                    >
                                        {r.almacenDestinoNombre || "-"}
                                    </Typography>
                                </Box>

                                {/* Información adicional */}
                                <Stack spacing={0.75}>
                                    {/* Fecha */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <CalendarTodayIcon sx={{ fontSize: 14, color: "#6c757d" }} />
                                        <Typography sx={{ fontSize: 12, color: "#6c757d", fontWeight: 600 }}>
                                            Fecha:
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: "#495057" }}>
                                            {r.fecha ? String(r.fecha).slice(0, 10) : "-"}
                                        </Typography>
                                    </Box>

                                    {/* Unidades y Líneas */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <InventoryIcon sx={{ fontSize: 14, color: "#6c757d" }} />
                                            <Typography sx={{ fontSize: 12, color: "#6c757d", fontWeight: 600 }}>
                                                Unidades:
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: "#495057", fontWeight: 600 }}>
                                                {r.totalUnidades ?? 0}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 12, color: "#6c757d" }}>·</Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography sx={{ fontSize: 12, color: "#6c757d", fontWeight: 600 }}>
                                                Líneas:
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: "#495057", fontWeight: 600 }}>
                                                {r.totalLineas ?? r.lineasCount ?? 0}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Empleados */}
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                        <PersonIcon sx={{ fontSize: 14, color: "#6c757d", mt: 0.2 }} />
                                        <Box>
                                            <Typography sx={{ fontSize: 11, color: "#6c757d", fontWeight: 600 }}>
                                                Origen: {r.empleadoOrigenNombre || "-"}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: "#6c757d", fontWeight: 600 }}>
                                                Destino: {r.empleadoDestinoNombre || "-"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Stack>

                                {/* Acciones */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        mt: 2,
                                        pt: 1.5,
                                        borderTop: "1px solid #e9ecef",
                                    }}
                                >
                                    <IconButton
                                        onClick={() => onVer(r)}
                                        size="small"
                                        sx={{
                                            color: "#6c757d",
                                            backgroundColor: "#f8f9fa",
                                            "&:hover": {
                                                backgroundColor: "#e9ecef",
                                            },
                                        }}
                                    >
                                        <VisibilityIcon sx={{ fontSize: 18 }} />
                                    </IconButton>

                                    {!isTraspasado && (
                                        <>
                                            <IconButton
                                                onClick={() => onEditar(r)}
                                                size="small"
                                                sx={{
                                                    color: "#495057",
                                                    backgroundColor: "#f8f9fa",
                                                    "&:hover": {
                                                        backgroundColor: "#e9ecef",
                                                    },
                                                }}
                                            >
                                                <EditIcon sx={{ fontSize: 18 }} />
                                            </IconButton>

                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<TaskAltIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => onTraspasar(r)}
                                                sx={{
                                                    fontSize: 12,
                                                    px: 1.5,
                                                    py: 0.5,
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    backgroundColor: "#198754",
                                                    "&:hover": {
                                                        backgroundColor: "#157347",
                                                    },
                                                }}
                                            >
                                                Traspasar
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Card>
                    );
                })}
            </Stack>

            {/* Paginación */}
            {totalPages > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 3,
                        pb: 2,
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        size="large"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                fontSize: 14,
                            },
                            "& .Mui-selected": {
                                backgroundColor: "#cc071e !important",
                                color: "#fff",
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}
